<template>
    <div class="col-12 nopads">
        <div v-if="tableItems.length > 0" class="row col-12 nopads">
            <div class="col-sm-6 nopads pt-2 pl-3">
                <div class="row col-12 nopads">
                    <div class="col" v-if="!invoicingFieldsDefault">
                        <div class="form-check form-switch">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="invoicingToggle"
                                v-model="showInvoicingFields"
                            />
                            <label class="form-check-label" for="invoicingToggle">
                                {{ $t('daily_summary.show_invoicing_functions') }}
                            </label>
                        </div>
                    </div>
                    <div class="col" v-if="!hideDetailButton">
                        <div class="form-check form-switch">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="detailsToggle"
                                v-model="showDetails"
                            />
                            <label class="form-check-label" for="detailsToggle">
                                {{ $t('trip_list.details') }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 nopads p-1 d-flex justify-content-end align-items-center">
                <!-- Small red dot conditionally displayed -->
                <span
                    v-if="showInvoicingFields && !(Object.keys(this.changedInvoiceableSums).length === 0 && invoiceStatesToBeChanged.length < 1 && !loading)"
                    class="dot"
                    style="width: 10px; height: 10px; background-color: red; border-radius: 50%; display: inline-block; margin-right: 8px;"
                ></span>
                <button
                    v-if="showInvoicingFields && canAddManualRow && allowManualRecords"
                    class="btn btn-sm btn-outline-primary form-button dont-print"
                    :disabled="loading || hasUnsavedInvoicingChanges"
                    :title="hasUnsavedInvoicingChanges ? $t('daily_summary.unsaved_invoicing_changes_warning') : ''"
                    @click="showManualTripRecordEditor = true"
                >
                    {{ $t('daily_summary.add_new_row') }}
                </button>
                <button
                    v-if="showInvoicingFields"
                    class="btn btn-sm btn-primary form-button dont-print"
                    :disabled="Object.keys(this.changedInvoiceableSums).length === 0 && invoiceStatesToBeChanged.length < 1 && !loading"
                    @click="confirmInvoiced = true"
                >
                    {{ $t('daily_summary.set_invoiced_state') }}
                </button>
                <div
                    v-if="!hideExcelButton"
                    class="dropdown"
                >
                    <button
                        class="btn btn-sm btn-outline-success dropdown-toggle form-button dont-print"
                        type="button"
                        data-bs-toggle="dropdown"
                    >
                        {{ $t('trip.download_excel') }}
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                        <li>
                            <button
                                class="dropdown-item"
                                type="button"
                                @click="exportExcel(false)"
                            >
                                {{ $t('trip.download_full_excel') }}
                            </button>
                        </li>
                        <li>
                            <button
                                class="dropdown-item"
                                type="button"
                                @click="exportExcel(true)"
                            >
                                {{ $t('trip.download_trimmed_excel') }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="col-sm-12">
            <div v-if="tableItems && tableItems.length > 0" class="tableDiv">
                    <vue3-datatable
                        :rows="tableItems"
                        :columns="fields"
                        :pagination="false"
                        :sticky-header="true"
                        :height="tableHeight ? tableHeight : tableItems.length > 10 ? '60vh' : '100%'"
                        :sortable="true"
                        sortColumn=""
                        skin="bh-table-compact"
                        :row-class="getRowClass"
                        cell-class="routa-table-cell"
                        @row-click="showTrips"
                        :class="{'small-text' : smallText}"
                    >
                        <template #date="data">
                            {{ data.value.date ? getShortDateString(data.value.date) : "-" }}
                        </template>
                        <template #tripnote="data">
                            <span v-if="data.value.tripnotes?.length">
                                <span
                                    v-for="note in data.value.tripnotes"
                                    :key="note.id"
                                    class="d-block"
                                >
                                    <strong>{{ toLocalTime(note.start_time) }}–{{ toLocalTime(note.end_time) }}</strong>: {{ note.note }}
                                </span>
                                <!--
                                <font-awesome-icon icon="comment" class="note" />
                                -->
                            </span>
                        </template>
                        <template #invoice_state="data">
                            <select
                                :id="data.value.id + 'invoice_state'"
                                class="form-select form-select-sm"
                                :class="{'small-input-text' : smallText}"
                                style="min-width: 10em; border: 1px solid #bcbcbc; border-radius: .2em;"
                                v-model="invoiceSelectStates[data.value.id]"
                                :disabled="data.value.order === '-' || data.value.invoice_state === invoiceStates().INVOICED || (!isCustomerOrMainContractor(data) && data.value.invoiceable_sum === '-')"
                                @change="changeRowInvoiceState(data.value.id, invoiceSelectStates[data.value.id], data.value.invoice_state)"
                                @click.stop
                            >
                                <option :value="null">
                                    {{ $t('daily_summary.select_invoice_state') }}
                                </option>
                                <option
                                    v-for="option in invoiceStateOptions"
                                    :key="option.value"
                                    :value="option.value"
                                    :disabled="isOptionDisabled(option, data.value.invoice_state, data)"
                                >
                                    {{ option.text }}
                                </option>
                            </select>
                        </template>
                        <template #invoice_history="data">
                            <div class="nopads text-center" @click.stop="isCustomerOrMainContractor(data) && showInvoiceHistory(data.value)">
                                <font-awesome-icon
                                    class="mt-2"
                                    prefix="fa"
                                    icon="clock"
                                    @click.stop="isCustomerOrMainContractor(data) && showInvoiceHistory(data.value)"
                                />
                            </div>
                        </template>

                        <template #work_distance="data">
                            <span v-bind:class="{ dangerText: highlightDistance(data.value.work_distance, data.value.covered_road_distance) }"> {{ data.value.work_distance }} </span>
                        </template>
                        <template #covered_road_distance="data">
                            <span v-bind:class="{ dangerText: highlightDistance(data.value.work_distance, data.value.covered_road_distance) }">
                                {{ data.value.covered_road_distance }}
                            </span>
                            <font-awesome-icon
                                v-if="shouldShowRoadKmWarning(data)"
                                icon="exclamation-circle"
                                class="defect mx-1"
                            />
                        </template>
                        <template #contract="data">
                            <span @click.stop="showContract(data.value)" class="cell-link">{{ data.value.contract}}</span>
                        </template>
                        <template #order="data">
                            <span @click.stop="showOrder(data.value)" class="cell-link">{{ data.value.order }}</span>
                        </template>
                        <template #invoice_amount="data">
                            <div v-if="isDeliveryListBased(data.value.pricing_model)" class="nopads">
                                <span v-if="data.value.delivery_tons && data.value.delivery_tons > 0"
                                    @click.stop="showDeliveries(data.value)" class="cell-link">
                                    {{ data.value.delivery_tons.toFixed(2) }} t
                                </span>
                                <span v-if="data.value.delivery_cubics && data.value.delivery_cubics > 0"
                                      @click.stop="showDeliveries(data.value)" class="cell-link">
                                    {{ data.value.delivery_cubics.toFixed(2) }} m3
                                </span>
                            </div>
                            <div v-else class="nopads">
                                <div :id="data.value.id + 'invoiceable_amount'">
                                    {{ data.value.invoice_amount }}
                                    <font-awesome-icon
                                        v-if="shouldShowRoadKmWarning(data)"
                                        icon="exclamation-circle"
                                        class="defect mx-1"
                                    />
                                </div>
                            </div>
                        </template>
                        <template #invoiceable_amount="data">
                            <div class="nopads">
                                <div
                                    :id="data.value.id + 'invoiceable_amount'"
                                    :class="{'cell-link': !disabledState(data.value), '': data.value.invoice_state === invoiceStates().INVOICED}"
                                    @click.stop="data.value.invoice_state !== invoiceStates().INVOICED && !disabledState(data.value) ? selectInvoiceableSum(data.value) : null"
                                >
                                    {{ data.value.invoiceable_amount }}
                                </div>
                            </div>
                        </template>
                        <template #delivery_tons="data">
                            <span v-if="data.value.delivery_tons && data.value.delivery_tons > 0"  @click.stop="showDeliveries(data.value)" class="cell-link">
                                {{ data.value.delivery_tons.toFixed(2) }}
                            </span>
                            <span v-if="data.value.delivery_cubics && data.value.delivery_cubics > 0" @click.stop="showDeliveries(data.value)" class="cell-link">
                                {{ data.value.delivery_cubics.toFixed(2) }} m3
                            </span>
                        </template>

                    </vue3-datatable>
                </div>
        </div>

        <base-modal
            v-if="showChangeInvoiceableSumModal"
            :title="$t('daily_summary.confirm_invoiceable_amount')"
            :hide-footer="true"
            size="sm"
            :prevent-close="true"
            :hide-header-close="true"
            @ok="changeInvoiceableSum(selectedSummaryRow)"
            @close="showChangeInvoiceableSumModal = false"
        >
            <div class="col-12">
                <div class="col-12">
                    <span>{{ $t('daily_summary.invoiceable_amount')}}</span>
                    <input
                        id="invoiceableAmount"
                        v-model="selectedSummaryRow.invoiceable_amount"
                        type="number"
                        step="any"
                        class="form-control form-control-sm"
                    />
                </div>
                <div class="col-12">
                    <span>{{ $t('daily_summary.invoiceable_amount_explanation')}}</span>
                    <textarea
                        id="invoiceExplanation"
                        v-model="selectedSummaryRow.invoice_explanation"
                        class="form-control form-control-sm"
                        rows="3"
                    ></textarea>
                </div>
                <div class="col-12 button-container">
                    <button class="btn btn-sm btn-secondary form-button" @click="showChangeInvoiceableSumModal = false">
                        {{ $t('common.no') }}
                    </button>
                    <button class="btn btn-sm btn-success form-button" @click="changeInvoiceableSum(selectedSummaryRow)">
                        {{ $t('common.yes') }}
                    </button>
                </div>
            </div>
        </base-modal>

        <base-modal
            v-if="confirmInvoiced"
            :title="$t('daily_summary.confirm_invoice_states')"
            :hide-footer="true"
            @close="confirmInvoiced = false"
        >
            <span>{{ $t('daily_summary.confirm_invoiced_description')}}</span>
            <div class="col-12 button-container">
                <button class="btn btn-sm btn-secondary form-button" @click="confirmInvoiced = false">
                    {{ $t('common.no') }}
                </button>
                <button class="btn btn-sm btn-success form-button" @click="submitInvoiceChanges">
                    {{ $t('common.yes') }}
                </button>
            </div>
        </base-modal>
        <base-modal
            v-if="showReturnExplanationModal"
            ref="invoiceReturnExplanationModal"
            :title="$t('daily_summary.confirm_return_explanation')"
            :hide-header-close="true"
            :hide-footer="true"
            @close="closeReturnModal(true)"
        >
            <div class="nopads">
                <span>{{ $t('daily_summary.return_explanation') }}</span>
                <textarea
                    id="returnExplanation"
                    v-model="returnExplanation"
                    class="form-control form-control-sm"
                    rows="3"
                />
                <small v-if="returnExplanation.length > 0 && returnExplanation.length < 5" class="text-danger">
                        {{ $t('common.min_chars', { count: 5 }) }}
                </small>
            </div>
            <div class="col-12 button-container">
                <button class="btn btn-sm btn-secondary form-button" @click="closeReturnModal(true)">
                    {{ $t('common.cancel') }}
                </button>
                <button
                    class="btn btn-success btn-sm form-button"
                    :disabled="returnExplanation.length < 5"
                    @click="submitReturnExplanation"
                >
                    {{ $t('common.yes') }}
                </button>
            </div>
        </base-modal>

        <!-- Manual Trip Record Editor -->
        <manual-trip-record-editor
            v-if="showManualTripRecordEditor"
            :user="user"
            :search-params="params"
            @close="showManualTripRecordEditor = false"
            @saved="handleManualRecordSaved"
        />
    </div>
</template>

<script>
import {timeUtils} from "@/components/mixins/TimeUtils";
import {invoiceStates, roles} from "@/components/invoice/InvoiceMixin";
import {vehicleHelper} from "@/components/mixins/VehicleMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import {downloadHelper} from "@/components/mixins/DownloadMixin";
import {priceHelper} from "@/components/mixins/PricingMixin";
import Vue3Datatable from '@bhplugin/vue3-datatable'
import BaseModal from "@/components/BaseModal";
import ManualTripRecordEditor from "@/components/tripsummary/ManualTripRecordEditor";
import {inject} from "vue";

export default {
    name: "contract-trip-summary-list",
    components: {BaseModal, Vue3Datatable, ManualTripRecordEditor},
    mixins: [restApi, timeUtils, vehicleHelper, downloadHelper, invoiceStates, priceHelper],
    props: {
        summaries: {
            type: Array,
            default() {
                return []
            }
        },
        invoiceStateOptions: {
            type: Array,
            default() {
                return []
            }
        },
        params: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        smallText: {
            type: Boolean,
            default: false
        },
        invoicingFieldsDefault: {
            type: Boolean,
            default: false
        },
        hideExcelButton: {
            type: Boolean,
            default: false
        },
        hideDetailButton: {
            type: Boolean,
            default: false
        },
        tableHeight: {
            type: String,
            default: null
        },
        allowManualRecords: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            tableItems: [],
            changedInvoiceableSums: {},
            totalInvoiceableSum: 0,
            totalAmount: 0,
            contentWidth: 2000, // To dynamically calculate table width
            selectedSummaryRow: {},
            showChangeInvoiceableSumModal: false,
            showReturnExplanationModal: false,
            currentReturnItemId: null,
            returnOriginalValue: null,
            returnExplanation: "",
            invoiceStatesToBeChanged: [],
            invoiceSelectStates: {},
            confirmInvoiced: false,
            loading: false,
            showInvoicingFields: false,
            showDetails: false,
            selectMode: 'single',
            contractorInValidStates: [
                invoiceStates.READY_FOR_INVOICING,
                invoiceStates.APPROVED_FOR_INVOICING,
                invoiceStates.INVOICED
            ],
            fields: [],
            showManualTripRecordEditor: false
        }
    },
    computed: {
        canAddManualRow() {
            // Only contractor's supervisor can add manual rows
            const isSupervisor = this.user.roles.some(role => role.role === 2);
            const isContractor = this.user.company && this.user.company.id;
            return isSupervisor && isContractor;
        },
        hasUnsavedInvoicingChanges() {
            return Object.keys(this.changedInvoiceableSums).length > 0 ||
                   this.invoiceStatesToBeChanged.length > 0;
        },
        defaultFields() {
            return [
                { field: 'date', title: this.$t('daily_summary.date'), sort: true, headerClass: 'routa-table-header' },
                { field: 'company', title: this.$t('daily_summary.company'), sort: true, headerClass: 'routa-table-header' },
                { field: 'driver', title: this.$t('trip_list.driver'), sort: true, headerClass: 'routa-table-header' },
                { field: 'vehicle', title: this.$t('trip_list.vehicle'), sort: true, headerClass: 'routa-table-header' },
                { field: 'customer', title: this.$t('contracts.customer'), sort: true, headerClass: 'routa-table-header' },
                { field: 'contract', title: this.$t('orders.contract'), sort: true, headerClass: 'routa-table-header' },
                { field: 'order', title: this.$t('vehicle_position.order'), sort: true, headerClass: 'routa-table-header' },
                { field: 'order_info', title: this.$t('common.info'), sort: true, minWidth: '30em', headerClass: 'routa-table-header' },
                { field: 'invoice_amount', title: this.$t('trip_list.amount'), sort: true, headerClass: 'routa-table-header' },
                { field: 'invoice_unit', title: this.$t('trip_list.unit'), sort: true, headerClass: 'routa-table-header' },
                { field: 'unit_price', title: this.$t('trip_list.unit_price'), sort: true, headerClass: 'routa-table-header' },
                { field: 'cost', title: this.$t('daily_summary.cost'), sort: true, headerClass: 'routa-table-header' },
                { field: 'tripnote', title: this.$t('daily_summary.trip_notes'), minWidth: '20em', sort: false, headerClass: 'routa-table-header' }
            ]
        },
        detailedFields() {
            return [
                { field: 'date', title: this.$t('daily_summary.date'), sort: true, headerClass: 'routa-table-header' },
                { field: 'company', title: this.$t('daily_summary.company'), sort: true, headerClass: 'routa-table-header' },
                { field: 'driver', title: this.$t('trip_list.driver'), sort: true, headerClass: 'routa-table-header' },
                { field: 'vehicle', title: this.$t('trip_list.vehicle'), sort: true, headerClass: 'routa-table-header' },
                { field: 'customer', title: this.$t('contracts.customer'), sort: true, headerClass: 'routa-table-header' },
                { field: 'contract', title: this.$t('orders.contract'), sort: true, headerClass: 'routa-table-header' },
                { field: 'order', title: this.$t('vehicle_position.order'), sort: true, headerClass: 'routa-table-header' },
                { field: 'order_info', title: this.$t('common.info'), sort: true, minWidth: '30em', headerClass: 'routa-table-header' },
                { field: 'duration', title: this.$t('trip_list.duration'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'work_duration', title: this.$t('trip_list.work_duration'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'allocation', title: this.$t('trip_list.vehicle_allocation'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'work_distance', title: this.$t('daily_summary.work_distance'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'resettle_distance', title: this.$t('daily_summary.resettle_distance'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'covered_road_distance', title: this.$t('daily_summary.road_distance'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'square_meters', title: this.$t('areas.square_meters'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'avg_speed', title: this.$t('trip_list.avg_speed'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'delivery_tons', title: this.$t('daily_summary.delivery_tons'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'invoice_amount', title: this.$t('trip_list.amount'), type: 'number', sort: true, headerClass: 'routa-table-header' },
                { field: 'invoice_unit', title: this.$t('trip_list.unit'), sort: true, headerClass: 'routa-table-header' },
                { field: 'unit_price', title: this.$t('trip_list.unit_price'), sort: true, headerClass: 'routa-table-header' },
                { field: 'cost', title: this.$t('daily_summary.cost'), sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'tripnote', title: this.$t('daily_summary.trip_notes'), sort: false, minWidth: '20em', headerClass: 'routa-table-header' }
            ]
        },
        invoicingFields() {
            return [
                { field: 'invoiceable_amount', title: this.$t('trip_list.amount_to_invoice'), sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'invoiceable_sum', title: this.$t('daily_summary.invoiceable_sum'), sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'invoice_explanation', title: this.$t('daily_summary.invoice_explanation'), sort: true, headerClass: 'routa-table-header' },
                { field: 'return_explanation', title: this.$t('daily_summary.return_explanation'), sort: true, headerClass: 'routa-table-header' },
                { field: 'invoice_state', title: this.$t('daily_summary.invoice_state'), sort: true, headerClass: 'routa-table-header' },
                { field: 'invoice_history', title: this.$t('daily_summary.invoice_history'), sort: true, headerClass: 'routa-table-header', cellClass: 'justify-center' }
            ]
        }
    },
    watch: {
        summaries: function () {
            this.getTableItems()
        },
        showInvoicingFields () {
           this.updateTableFields()
        },
        showDetails() {
            this.updateTableFields()
        },
        '$i18n.locale'() {
            this.updateTableFields()
        }
    },
    mounted() {
        this.updateTableFields()
        this.getTableItems()
        if(this.invoicingFieldsDefault) {
            this.showInvoicingFields = true
        }
    },
    methods: {
        updateTableFields() {
            let fields
            if (this.showInvoicingFields) {
                let visibleFields = this.showDetails ? this.detailedFields : this.defaultFields
                fields = [
                    ...visibleFields.slice(0, visibleFields.length-1),
                    ...this.invoicingFields,
                    ...visibleFields.slice(visibleFields.length-1)
                    ]
            } else {
                fields = this.showDetails ? this.detailedFields : this.defaultFields
            }
            this.fields = fields
        },
        invoiceStates() {
            return invoiceStates
        },
        disabledState(row) {
            const superVisor = this.user.roles.some(role => role.role === 2);
            const canEdit = row.contractor_id === this.user.company.id && superVisor;
            const isInvalidState = this.contractorInValidStates.includes(row.invoice_state);
            return !(canEdit && !isInvalidState);
        },
        isCustomerOrMainContractor(invoiceRow) {
            if (!invoiceRow.value) return false;
            const isCustomer = invoiceRow.value.customer_id === this.user.company.id;
            const isContractor = invoiceRow.value.contractor_id === this.user.company.id;

            return isCustomer || isContractor
        },
        setInvoiceState(invoiceId, invoiceStateValue) {
            this.invoiceSelectStates[invoiceId] = invoiceStateValue
            return invoiceStateValue
        },
        isOptionDisabled(option, currentInvoiceState, invoiceRow) {
            const isCustomer = invoiceRow.value.customer_id === this.user.company.id;
            const isContractor = invoiceRow.value.contractor_id === this.user.company.id;

            if (isCustomer && isContractor) {
                // Check if neither roles.CONTRACTOR nor roles.CUSTOMER are present
                return ![roles.CONTRACTOR, roles.CUSTOMER].some(role => option.stateChangeAllowedForRoles.includes(role));
            } else {
                // Disable specific options for customer if state change isn't allowed
                if (isCustomer && !option.stateChangeAllowedForRoles.includes(roles.CUSTOMER)) {
                    return true;
                }
                // Disable specific options for contractor if state change isn't allowed
                if (isContractor && !option.stateChangeAllowedForRoles.includes(roles.CONTRACTOR)) {
                    return true;
                }
            }
            return false;
        },
        getTableItems: function () {
            this.tableItems = []
            this.totalInvoiceableSum = 0
            this.totalAmount = 0
            const temp = []
            if (this.summaries.length > 0) {
                this.summaries.forEach(function (item) {
                    this.totalInvoiceableSum += item.invoiceable_sum ? item.invoiceable_sum : item.cost
                    this.totalAmount += item.invoice_amount
                    temp.push({
                        id: item.id,
                        date: item.date,
                        company: item.company,
                        driver: item.driver,
                        driver_id: item.driver_id,
                        vehicle: item.vehicle,
                        customer: item.customer,
                        customer_id: item.customer_id,
                        contract: item.contract,
                        contract_id: item.contract_id,
                        contractors: item.contractor,
                        contractor_id: item.contractor_id,
                        order: item.order ? item.order.task_type.name + ' (' + item.order.id +')' : '-',
                        order_info: item.order && item.order.info ? item.order.info.length > 200 ? item.order.info.substr(0, 200) + '...' : item.order.info : '-',
                        area: item.area ? item.area : '-',
                        area_id: item.area_id ? item.area_id : undefined,
                        work_distance: parseFloat(item.work_distance < 10 ?
                            item.work_distance.toFixed(2) :
                            item.work_distance.toFixed(1)).toLocaleString(),
                        resettle_distance: parseFloat(item.resettle_distance < 10 ?
                            item.resettle_distance.toFixed(2) :
                            item.resettle_distance.toFixed(1)
                        ).toLocaleString(),
                        covered_road_distance: item.covered_road_distance ? parseFloat(item.covered_road_distance.toFixed(2)).toLocaleString() : '',
                        duration: this.getWorkTime(Math.round(item.duration / 60)),
                        work_duration: this.getWorkTime(Math.round(item.work_duration / 60)),
                        square_meters: Math.round(item.square_meters),
                        allocation: this.getWorkTime(item.vehicle_allocation_time),
                        delivery_tons: item.delivery_tons,
                        delivery_cubics: item.delivery_cubics,
                        avg_speed: this.getAverageSpeed(item.work_distance + item.resettle_distance, item.duration / 60),
                        co2_emissions: (item.co2_emissions && item.co2_emissions > 0) ? Math.round(item.co2_emissions * (item.work_distance + item.resettle_distance))/1000.0 : '-',
                        cost: item.cost ? item.cost : '-',
                        invoiced: item.invoiced,
                        invoiceable_sum: item.invoiceable_sum ? item.invoiceable_sum : '-',
                        invoice_explanation: item.invoice_explanation,
                        return_explanation: item.return_explanation,
                        invoice_state: this.setInvoiceState(item.id, item.invoice_state),
                        tripnotes: item.tripnotes,
                        unhandled_coverage_areas: item.unhandled_coverage_areas,
                        invoice_unit: item.pricing_model ? this.pricingModelToTextNoCurrency(item.pricing_model) : '-',
                        invoice_amount: item.invoice_amount ? item.invoice_amount.toFixed(2) : '-',
                        invoiceable_amount: item.invoiceable_amount ? item.invoiceable_amount.toFixed(2) : item.invoice_amount ? item.invoice_amount.toFixed(2) : 0,
                        unit_price: item.unit_price ? (item.unit_price / 100).toFixed(3) :'-',
                        pricing_model: item.pricing_model,
                        is_manual_record: item.is_manual_record || false
                    })
                }, this)
                this.$nextTick(() => this.tableItems = temp)
            }
        },
        getRowClass(row) {
            if (row.is_manual_record) {
                return 'routa-table-row manual-trip-row';
            }
            return 'routa-table-row';
        },
        handleManualRecordSaved(response) {
            // Refresh the trip summary list to show the new manually added row
            this.$emit('updateRequest');
        },
        isDeliveryListBased(pricingModel) {
            return pricingModel &&
                (pricingModel.unit1 === 5 || pricingModel.unit1 === 6)
                && pricingModel.unit2 === 103
        },
        selectInvoiceableSum(item) {
            // ensure there is some value in invoiceable_amount
            if (!item.invoiceable_amount) {
                let invoiceAmount = parseFloat(item.invoice_amount);
                item.invoiceable_amount = !isNaN(invoiceAmount) ? invoiceAmount.toFixed(2) : '0';
            }
            this.showChangeInvoiceableSumModal = true
            this.selectedSummaryRow = {
                ...item,
                invoiceable_amount: item.invoiceable_amount,
                invoiceable_sum: item.invoiceable_sum,
                invoice_explanation: item.invoice_explanation || '', // Use invoice_explanation if available, otherwise use an empty string
                return_explanation: item.return_explanation || ''
            };
        },
        exportExcel: function (getTrimmedVersion) {
            this.$emit('loading', true)

            this.params.trim = getTrimmedVersion ? 1 : 0
            this.restGetDownloadFile(this.contractDailyTripSummaryReportUrl, this.getCommonReportParams(this.params), this.fileDownloadSuccess, this.fileDownloadFail)
        },
        fileDownloadSuccess: function (response) {
            this.$emit('loading', false)
            this.downloadFile(response, this.$t('daily_summary.report_file_name'))
        },
        fileDownloadFail: function () {
            this.$emit('loading', false)
            this.showAlert(this.$t('common.report_generation_failed'))
        },
        highlightDistance: function (workDistance, roadDistance) {
            return roadDistance ? workDistance / roadDistance >= 3 : false;
        },
        shouldShowRoadKmWarning(data) {
            // return true if there are unhandled coverage areas
            return data.value.unhandled_coverage_areas ||
                   // check if there is work distance
                   (data.value.work_distance > 0 &&
                    // check if covered_road_distance is 0 by any way
                    (!data.value.covered_road_distance|| data.value.covered_road_distance === 0 || isNaN(Number(data.value.covered_road_distance))) &&
                    data.value.pricing_model &&
                    // check if pricing models unit1 is ROAD KM or ROAD M
                    ((data.value.pricing_model.unit1 === 4 || data.value.pricing_model.unit1 === 10)))
        },
        showTrips: function (row, data) {
            // Don't allow clicking on manually added rows (they have no trips)
            if (!this.loading && !row.is_manual_record) {
                let selectedItem = this.summaries.find(item => item.id === row.id)
                if (selectedItem){
                    this.$emit('rowSelected', selectedItem)
                }
            }
        },
        showDeliveries: function (row) {
            if (!this.loading) {
                let selectedItem = this.summaries.find(item => item.id === row.id)
                if (selectedItem){
                    this.$emit('deliveryTonsClicked', selectedItem)
                }
            }
        },
        showInvoiceHistory: function (row) {
            if (!this.loading) {
                let selectedItem = this.summaries.find(item => item.id === row.id)
                if (selectedItem){
                    this.$emit('invoiceHistoryClicked', selectedItem)
                }
            }
        },
        showContract: function (row) {
            if (!this.loading) {
                let selectedItem = this.summaries.find(item => item.id === row.id)
                if (selectedItem){
                    this.$emit('contractClicked', selectedItem)
                }
            }
        },
        showOrder: function (row) {
            if (!this.loading) {
                let selectedItem = this.summaries.find(item => item.id === row.id)
                if (selectedItem){
                    this.$emit('orderClicked', selectedItem)
                }
            }
        },
        changeRowInvoiceState(itemId, updateStateValue, originalStateValue) {
            if (!updateStateValue) return; // Early return if updateStateValue is null or undefined

            const existingIndex = this.invoiceStatesToBeChanged.findIndex(
                (obj) => obj.id === itemId
            );

            if (existingIndex === -1) {
                // Item doesn't exist, add it to the array
                this.invoiceStatesToBeChanged.push({ id: itemId, updateStateValue });

                if (updateStateValue === invoiceStates.REJECTED) {
                    this.setReturnModal(itemId, originalStateValue, "", true); // Open rejection modal
                }
            } else {
                // Item already exists
                if (updateStateValue !== originalStateValue) {
                    // Update the state if it's changed
                    this.invoiceStatesToBeChanged[existingIndex].updateStateValue = updateStateValue;

                    if (updateStateValue === invoiceStates.REJECTED) {
                        const existingExplanation =
                            this.invoiceStatesToBeChanged[existingIndex].return_explanation || "";
                        this.setReturnModal(itemId, originalStateValue, existingExplanation, true);
                    }
                } else {
                    // If the state hasn't changed, remove the item
                    this.invoiceStatesToBeChanged.splice(existingIndex, 1);
                }
            }
        },
        setReturnModal(itemId, originalStateValue, existingExplanation, openReturnModal) {
            // Helper method to open the return modal
            this.currentReturnItemId = itemId; // item id
            this.returnOriginalValue = originalStateValue; // store the original state value for possible revert
            this.returnExplanation = existingExplanation;
            this.showReturnExplanationModal = openReturnModal; // open the return modal
        },
        // Submit explanation from modal
        submitReturnExplanation() {
            const index = this.invoiceStatesToBeChanged.findIndex(
                (obj) => obj.id === this.currentReturnItemId
            );
            if (index >= 0) {
                // Add the explanation to the corresponding object
                this.invoiceStatesToBeChanged[index].return_explanation = this.returnExplanation;
                if(this.tableItems.length > 0) {
                    this.tableItems.forEach(tableItem => {
                        if(tableItem.id === this.currentReturnItemId) {
                            tableItem.return_explanation = this.returnExplanation
                        }
                    })
                }
            }

            this.closeReturnModal();
        },

        // Close modal
        closeReturnModal(undoChanges = false) {
            if(undoChanges) {
                const existingIndex = this.invoiceStatesToBeChanged.findIndex(
                    (obj) => obj.id === this.currentReturnItemId
                );
                // revert changes if needed
                this.invoiceStatesToBeChanged.splice(existingIndex, 1);
                this.invoiceSelectStates[this.currentReturnItemId] = this.returnOriginalValue
            }
            this.setReturnModal(null, null, "", false)
        },
        submitInvoiceChanges() {
            this.confirmInvoiced = false
            this.loading = true
            this.$emit('loading', true)
            this.restUpdate(this.setInvoiceChangesDailyTripSummaryUrl, {stateChanges: this.invoiceStatesToBeChanged, sumChanges: this.changedInvoiceableSums}, this.invoiceChangesSuccess, this.invoiceChangesFail)
        },
        invoiceChangesSuccess() {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('loading', false)
            this.changedInvoiceableSums = {}
            this.invoiceStatesToBeChanged = []
            this.$emit('updateRequest')
            this.$emit('showDetails', null)
        },
        invoiceChangesFail() {
            this.loading = false
            this.$emit('loading', false)
            this.showAlert(this.$t('common.alert_update_failed'))
        },
        changeInvoiceableSum(selectedSummaryRow) {
            this.showChangeInvoiceableSumModal = false
            if (!this.changedInvoiceableSums[selectedSummaryRow.id]) {
                this.changedInvoiceableSums[selectedSummaryRow.id] = {}; // Initialize the object if it doesn't exist
            }
            this.changedInvoiceableSums[selectedSummaryRow.id]['id'] = selectedSummaryRow.id
            this.changedInvoiceableSums[selectedSummaryRow.id]['invoiceable_amount'] = selectedSummaryRow.invoiceable_amount
            this.changedInvoiceableSums[selectedSummaryRow.id]['invoiceable_sum'] = selectedSummaryRow.unit_price != null && selectedSummaryRow.invoiceable_amount != null ? selectedSummaryRow.invoiceable_amount * (selectedSummaryRow.unit_price) : 0.0
            this.changedInvoiceableSums[selectedSummaryRow.id]['invoice_explanation'] = selectedSummaryRow.invoice_explanation
            if(this.tableItems.length > 0) {
                this.tableItems.forEach(tableItem => {
                    if(tableItem.id === selectedSummaryRow.id) {
                        tableItem.invoiceable_amount = selectedSummaryRow.invoiceable_amount
                        tableItem.invoiceable_sum = selectedSummaryRow.unit_price != null && selectedSummaryRow.invoiceable_amount != null ? selectedSummaryRow.invoiceable_amount * (selectedSummaryRow.unit_price) : 0.0
                        tableItem.invoice_explanation = selectedSummaryRow.invoice_explanation
                    }
                })
            }

        },
    }
}
</script>

<style>
.dangerText {
    font-weight: bold;
    color: #bb330e;
}
.small-text {
    font-size: .75rem !important;
}
.small-input-text {
    font-size: .8rem !important;
}
</style>
